import * as React from "react";
import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Paging } from "react-ui";
import { ColumnStack, Fit, RowStack } from "@skbkontur/react-stack-layout";

import { ErrorsListContainer as ErrorsList } from "../ErrorList/ErrorsListContainer";
import { PrintErrorLogButtonContainer } from "../PrintErrorLogButton/PrintErrorLogButtonContainer";
import { ERROR_LOG_MODAL_CLOSE, ERROR_LOG_MODAL_SWITCH_PAGE } from "../../../metricsAction";
import { useMetrics } from "../../../Internals/Metrics/MetricsProviderContext";
import { clamp } from "../../../../../../Common/IntRangeUtils";
import { emptyRef } from "../../../TypingUtils";
import { tryScrollIntoViewWithFallback } from "../../../../Helpers/BrowserHelpers";
import { ErrorLog } from "../../../../Engine/Core/ErrorManager/Utils/Models";
import { GroupHeader, GroupHeaderContent, GroupHeaderTitle } from "../ErrorList/ErrorsGroup/ErrorGroupHeader.styles";

import { ErrorLogCounters } from "./ErrorLogCounters/ErrorLogCounters";
import { CountersContainer, ListContainer } from "./ErrorLogModal.styles";
import { getCountLimit } from "./Utils/Utils";

export interface ErrorLogModalProps {
    onClose: () => void;
    errorLog: ErrorLog;
    hasInternalErrors?: boolean;
}

export function ErrorLogModal(props: ErrorLogModalProps): JSX.Element {
    const { errorLog } = props;

    const container = React.useRef<HTMLDivElement>(emptyRef);

    const countLimit = getCountLimit();
    const itemCount = errorLog.errorsTotalCount + errorLog.warningsTotalCount + errorLog.successesTotalCount;
    const pageCount = Math.ceil(itemCount / countLimit);
    const [pageIndex, setPageIndex] = React.useState(0);

    const track = useMetrics();
    const handleClose = () => {
        track(ERROR_LOG_MODAL_CLOSE);
        props.onClose();
    };

    const pageChangeHandler = (x: number) => {
        track(ERROR_LOG_MODAL_SWITCH_PAGE, { page: x });
        setPageIndex(x - 1);
        if (container.current != undefined) {
            tryScrollIntoViewWithFallback(container.current, { block: "center", inline: "center" });
        }
    };

    const showPagination = () => pageCount > 1 && errorLog.errorsTotalCount !== countLimit;

    return (
        <Modal ignoreBackgroundClick width={860} onClose={handleClose}>
            <ModalHeader>
                Подробный результат проверки отчёта
                <CountersContainer>
                    <ErrorLogCounters errorLog={props.errorLog} useExtendedErrorLog />
                </CountersContainer>
            </ModalHeader>
            <ModalBody>
                <ColumnStack gap={4} block stretch>
                    <Fit>
                        <ListContainer ref={container}>
                            {props.hasInternalErrors && (
                                <GroupHeader>
                                    <GroupHeaderContent fixed={false}>
                                        <GroupHeaderTitle>Отчет</GroupHeaderTitle>
                                    </GroupHeaderContent>
                                </GroupHeader>
                            )}
                            <ErrorsList
                                showJustification
                                errorLog={props.errorLog}
                                showSuccesses
                                useExtendedErrorLog={true}
                                onBeforeNavigateToControl={handleClose}
                                range={[pageIndex * countLimit, clamp((pageIndex + 1) * countLimit, 0, itemCount)]}
                                hideExternalChecks={showPagination() && pageCount > pageIndex + 1}
                            />
                        </ListContainer>
                    </Fit>
                    {showPagination() && (
                        <Fit>
                            <Paging
                                pagesCount={pageCount}
                                activePage={pageIndex + 1}
                                onPageChange={pageChangeHandler}
                            />
                        </Fit>
                    )}
                </ColumnStack>
            </ModalBody>
            <ModalFooter panel>
                <RowStack gap={2}>
                    <Fit>
                        <PrintErrorLogButtonContainer />
                    </Fit>
                    <Fit>
                        <Button onClick={handleClose} data-tid="ErrorLogModalCloseButton">
                            Закрыть
                        </Button>
                    </Fit>
                </RowStack>
            </ModalFooter>
        </Modal>
    );
}
